/**
 * Dashboard Mock Data
 *
 * These mocks are ONLY used as fallback when:
 * 1. USE_MOCK_DATA=true (development without backend)
 * 2. In unit tests
 * 3. When real API is unavailable
 *
 * Primary source: processHandlers/dashboard.ts with real backend data
 * Keep mocks MINIMAL - just enough for testing
 */

import { FacilitatorDashboardData, GamerDashboardData, DashboardInfoBox, DashboardSprintCard } from '../types/dashboard'
import { WeeklySessionData } from '../../interaxDesignSystemComponents/SessionItem'
import { FACILITATOR_ACTION_BOXES, FACILITATOR_INFO_BOX_CONFIG } from '../../features/facilitator/pages/Dashboard.utils'
import { GAMER_ACTION_BOXES, GAMER_INFO_BOX_CONFIG } from '../../features/gamer/pages/Dashboard.utils'

// Minimal test values only
const MOCK_INFO_BOX_VALUES = {
  activeSprints: { value: 2, change: '↑ 12%', changeType: 'increase' as const },
  upcomingSessions: { value: 3, change: '↑ 25%', changeType: 'increase' as const },
  gameSessionsLive: { value: 1, changeData: { count: 1 } },
  totalParticipants: { value: 12, change: '↑ 8%', changeType: 'increase' as const }
}

export const mockInfoBoxes: DashboardInfoBox[] = FACILITATOR_INFO_BOX_CONFIG.map((config) => {
  const mockValue = MOCK_INFO_BOX_VALUES[config.key as keyof typeof MOCK_INFO_BOX_VALUES]

  return {
    title: config.title,
    value: mockValue.value,
    change: 'change' in mockValue ? mockValue.change : undefined,
    changeType: 'changeType' in mockValue ? mockValue.changeType : undefined,
    status:
      config.key === 'gameSessionsLive'
        ? mockValue.value > 0
          ? config.statusLive
          : config.statusInactive
        : config.status,
    changeData: 'changeData' in mockValue ? mockValue.changeData : undefined
  }
})

// Keep minimal mocks for sessions/sprints
export const mockSessionItems: WeeklySessionData[] = [
  {
    day: 15,
    month: 'DEC',
    title: 'Test Session',
    teams: 1,
    participants: 4,
    status: 'upcoming',
    time: '2:00 PM'
  }
]

export const mockSprintCards: DashboardSprintCard[] = [
  {
    id: 'mock-sprint-1',
    status: 'active',
    title: 'Mock Sprint',
    details: [
      { label: 'facilitator.dashboard.sprintCard.participants', value: 4 },
      { label: 'facilitator.dashboard.sprintCard.teams', value: 1 }
    ],
    progress: 50
  }
]

// Mock Facilitator Dashboard Data
export const mockFacilitatorDashboard: FacilitatorDashboardData = {
  actionBoxes: FACILITATOR_ACTION_BOXES,
  infoBoxes: mockInfoBoxes,
  thisWeekSessions: mockSessionItems,
  activeSprints: mockSprintCards
}

// Minimal mock values for gamer testing
const MOCK_GAMER_INFO_VALUES = {
  myActiveSprints: { value: 1, change: '↑ 1 new sprint', changeType: 'increase' as const },
  upcomingSessions: { value: 2, change: 'Next: Tomorrow', changeType: 'neutral' as const },
  sessionsCompleted: { value: 3, change: '↑ 3 this week', changeType: 'increase' as const },
  learningScore: { value: '75%', change: '↑ 5% improvement', changeType: 'increase' as const }
}

// Mock Gamer Dashboard Data
export const mockGamerDashboard: GamerDashboardData = {
  actionBoxes: GAMER_ACTION_BOXES,
  infoBoxes: GAMER_INFO_BOX_CONFIG.map((config) => ({
    title: config.title,
    ...MOCK_GAMER_INFO_VALUES[config.key as keyof typeof MOCK_GAMER_INFO_VALUES]
  })),
  upcomingSessions: mockSessionItems.slice(0, 1),
  mySprints: mockSprintCards.slice(0, 1)
}
